import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ASSETS } from "../assets";
import { DISCORD_URL } from "@/constants/links";

const PILLS = ["WoW 3.3.5a", "D&D 5E", "Forgotten Realms", "100% RolePlay"];

const FACTS = [
  { icon: "fa-server", label: "Engine", value: "WoW 3.3.5a emulátor" },
  { icon: "fa-dice-d20", label: "Pravidla", value: "Mechaniky D&D 5E" },
  { icon: "fa-earth-europe", label: "Svět", value: "Forgotten Realms (Faerûn)" },
  { icon: "fa-masks-theater", label: "Styl hry", value: "RP s příměsí PvE" },
  { icon: "fa-coins", label: "Ekonomika", value: "Hráčské profese a obchod" },
  { icon: "fa-shield-halved", label: "Cechy", value: "Aktivní guildy a frakce" },
  { icon: "fa-calendar-days", label: "Eventy", value: "Pravidelné RP akce" },
  { icon: "fa-people-group", label: "Komunita", value: "Přátelská a vřelá parta" },
];

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = () => {
  return (
    <section
      id="top"
      className="relative w-full overflow-hidden pt-28 pb-16 lg:pt-32 lg:pb-20"
      data-testid="hero-section"
    >
      {/* Atmospheric castle background */}
      <div className="absolute inset-0 z-0">
        <img
          src={ASSETS.heroCastle}
          alt=""
          aria-hidden="true"
          fetchPriority="high"
          className="h-full w-full object-cover object-[65%_center] brightness-[0.85]"
        />
        <div className="absolute inset-0 bg-[#141417]/55" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#141417] via-[#141417]/70 to-[#141417]/25" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#141417] to-transparent" />
      </div>

      <div className="relative z-10 mx-auto grid max-w-[1584px] grid-cols-1 items-center gap-10 px-6 lg:grid-cols-12 lg:gap-8">
        {/* Left: message + CTAs */}
        <div className="lg:col-span-6">
          <motion.div
            {...fade(0)}
            className="mb-6 flex items-center gap-3"
          >
            <span className="h-px w-8 bg-[#F0801F]" />
            <span className="font-display text-xs font-500 uppercase tracking-[0.3em] text-[#F0801F]">
              Online WoW RolePlay Server · Forgotten Realms
            </span>
          </motion.div>

          <motion.h1
            {...fade(0.08)}
            className="font-display text-4xl font-700 uppercase leading-[1.4] tracking-tight sm:text-5xl lg:text-6xl"
          >
            <span className="block text-white">Zapomenuté říše.</span>
            <span className="mt-4 block text-[#F0801F] lg:mt-6">Skutečné příběhy.</span>
          </motion.h1>

          <motion.p
            {...fade(0.16)}
            className="mt-6 max-w-xl text-base leading-relaxed text-white/65"
          >
            Draconia RP je bezplatný online RolePlay server (WoW RP) na enginu
            WoW 3.3.5a, zasazený do světa Forgotten Realms a poháněný
            mechanikami D&D 5E. Neleveluje se ani negrinduje - hlavní náplní
            je společné vyprávění příběhů v české (CZ) RolePlay komunitě.
            Server je proto silně namodován, aby ctil lore i pravidla D&D.
          </motion.p>

          <motion.div {...fade(0.24)} className="mt-7 flex flex-wrap gap-2">
            {PILLS.map((p) => (
              <span
                key={p}
                className="rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 font-display text-xs font-500 uppercase tracking-[0.1em] text-white/75"
              >
                {p}
              </span>
            ))}
          </motion.div>

          <motion.div {...fade(0.32)} className="mt-8 flex flex-wrap gap-3">
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-discord-btn"
              className="inline-flex items-center gap-2 rounded-md bg-[#F0801F] px-6 py-3.5 font-display text-sm font-600 uppercase tracking-[0.12em] text-[#141417] transition-colors hover:bg-[#ff9438]"
            >
              <i className="fa-brands fa-discord" />
              Discord
            </a>
            <a
              href="#lore"
              data-testid="hero-wiki-btn"
              className="inline-flex items-center gap-2 rounded-md border border-white/20 px-6 py-3.5 font-display text-sm font-600 uppercase tracking-[0.12em] text-white transition-colors hover:border-[#F0801F] hover:text-[#F0801F]"
            >
              Wiki
            </a>
            <Link
              to="/jak-hrat"
              data-testid="hero-howto-btn"
              className="inline-flex items-center gap-2 rounded-md border border-white/20 px-6 py-3.5 font-display text-sm font-600 uppercase tracking-[0.12em] text-white transition-colors hover:border-[#F0801F] hover:text-[#F0801F]"
            >
              Jak hrát
            </Link>
          </motion.div>
        </div>

        {/* Right: quick overview card */}
        <motion.aside
          {...fade(0.35)}
          className="lg:col-span-6"
          data-testid="hero-overview-card"
        >
          <div className="rounded-xl border border-white/10 bg-[#1a1a1f] p-6 shadow-[0_24px_70px_rgba(0,0,0,0.55)] sm:p-8">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="font-display text-sm font-600 uppercase tracking-[0.2em] text-white">
                Rychlý přehled
              </h2>
              <span className="flex items-center gap-2 font-display text-xs uppercase tracking-[0.14em] text-[#4ade80]">
                <span className="h-2 w-2 rounded-full bg-[#4ade80] shadow-[0_0_8px_#4ade80]" />
                Online
              </span>
            </div>

            <dl className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">
              {FACTS.map((f) => (
                <div key={f.label} className="flex items-start gap-3">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md border border-[#F0801F]/30 text-[#F0801F]">
                    <i className={`fa-solid ${f.icon} text-sm`} />
                  </span>
                  <div className="min-w-0">
                    <dt className="font-display text-xs font-600 uppercase tracking-[0.14em] text-white/65">
                      {f.label}
                    </dt>
                    <dd className="truncate text-sm font-500 text-white/85">{f.value}</dd>
                  </div>
                </div>
              ))}
            </dl>

            <div className="mt-5 flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
              <span className="flex items-center gap-2 text-sm text-white/70">
                <i className="fa-solid fa-user-plus text-[#F0801F]" />
                289 registrovaných účtů
              </span>
              <span className="font-display text-xs uppercase tracking-[0.14em] text-white/45">
                Patch 3.3.5a
              </span>
            </div>

            <Link
              to="/jak-hrat"
              data-testid="hero-overview-join-link"
              className="group mt-5 inline-flex items-center gap-2 font-display text-sm font-600 uppercase tracking-[0.12em] text-[#F0801F]"
            >
              Jak se připojit
              <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </motion.aside>
      </div>
    </section>
  );
};
